import { MsalAuthenticationTemplate, MsalProvider, type MsalAuthenticationResult } from "@azure/msal-react";
import { EventType, InteractionType, PublicClientApplication, type AuthenticationResult } from "@azure/msal-browser";
import { FluentProvider, Text } from "@fluentui/react-components";
import * as React from "react";
import { loginRequest, msalConfig } from "./msalConfig";
import { primerDarkTheme, primer } from "../styles/primerDarkTheme";

const useLocalTokenBridge = import.meta.env.VITE_USE_LOCAL_TOKEN_BRIDGE === "true";
const localTokenBridgeUrl = import.meta.env.VITE_LOCAL_TOKEN_BRIDGE_URL ?? "http://localhost:3031/token";

export const msalInstance = new PublicClientApplication(msalConfig);

export interface TokenBridgeUser {
    name: string;
    username: string;
    tenantId: string;
}

let tokenBridgeUserResolve: (u: TokenBridgeUser | null) => void;
export const tokenBridgeUserReady: Promise<TokenBridgeUser | null> = new Promise(r => { tokenBridgeUserResolve = r; });

if (useLocalTokenBridge) {
    fetch(localTokenBridgeUrl)
        .then(res => res.ok ? res.json() : null)
        .then((body: { accessToken?: string } | null) => {
            if (!body?.accessToken) { tokenBridgeUserResolve(null); return; }
            const payloadB64 = body.accessToken.split(".")[1];
            if (!payloadB64) { tokenBridgeUserResolve(null); return; }
            const claims = JSON.parse(atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/")));
            tokenBridgeUserResolve({
                name: claims.name ?? claims.given_name ?? "",
                username: claims.upn ?? claims.preferred_username ?? claims.email ?? "",
                tenantId: claims.tid ?? "",
            });
        })
        .catch(() => tokenBridgeUserResolve(null));
} else {
    tokenBridgeUserResolve!(null);
}

if (!useLocalTokenBridge) {
    const RETURN_URL_KEY = "dashboard.returnUrl";
    const isAuthReturn =
        window.location.hash.includes("code=") ||
        window.location.hash.includes("error=") ||
        window.location.hash.includes("state=");
    const hasAccount = msalInstance.getAllAccounts().length > 0;
    const currentUrl =
        window.location.pathname + window.location.search + window.location.hash;
    if (!hasAccount && !isAuthReturn && currentUrl !== "/") {
        sessionStorage.setItem(RETURN_URL_KEY, currentUrl);
    }

    const cachedAccounts = msalInstance.getAllAccounts();
    if (!msalInstance.getActiveAccount() && cachedAccounts.length > 0) {
        msalInstance.setActiveAccount(cachedAccounts[0]);
    }

    msalInstance.addEventCallback((event) => {
        const payload = event.payload as AuthenticationResult | null;
        if (
            (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
            payload?.account
        ) {
            msalInstance.setActiveAccount(payload.account);

            const saved = sessionStorage.getItem(RETURN_URL_KEY);
            if (saved) {
                sessionStorage.removeItem(RETURN_URL_KEY);
                const here =
                    window.location.pathname + window.location.search + window.location.hash;
                if (saved !== here) {
                    window.history.replaceState(null, "", saved);
                    // Nudge React Router to re-read the location.
                    window.dispatchEvent(new PopStateEvent("popstate"));
                }
            }
        }
    });
}

const SignInPrompt: React.FC<{ error?: { message?: string | null } | null }> = ({ error }) => {
    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: "16px", backgroundColor: primer.canvas }}>
            <img src="/icons/app-icon.svg" alt="" style={{ width: 40, height: 40, opacity: 0.9 }} />
            <Text size={600} weight="semibold" style={{ color: primer.fg }}>Testing Agent Dashboard</Text>
            <Text size={300} style={{ color: primer.fgMuted, maxWidth: "520px", textAlign: "center" }}>
                Sign in with your Microsoft account to continue.
            </Text>
            {error && (
                <Text size={200} style={{ color: primer.red, maxWidth: "640px", textAlign: "center" }}>
                    {error.message}
                </Text>
            )}
            <Text size={200} style={{ color: primer.fgSubtle, maxWidth: "640px", textAlign: "center" }}>
                This requires the SPA redirect URI and delegated API permission to be configured correctly in Entra.
            </Text>
        </div>
    );
};

const AuthLoading: React.FC = () => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: "12px", backgroundColor: primer.canvas }}>
        <img src="/icons/app-icon.svg" alt="" style={{ width: 40, height: 40, opacity: 0.9 }} />
        <Text size={600} weight="semibold" style={{ color: primer.fg }}>Testing Agent Dashboard</Text>
        <Text size={300} style={{ color: primer.fgMuted }}>Signing you in…</Text>
    </div>
);

const AuthError: React.FC<MsalAuthenticationResult> = ({ error }) => <SignInPrompt error={error} />;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (useLocalTokenBridge) {
        return (
            <FluentProvider theme={primerDarkTheme} style={{ height: "100%", minHeight: "100vh" }}>
                {children}
            </FluentProvider>
        );
    }

    const isDebugRoute = window.location.pathname.startsWith("/auth-debug");

    return (
        <MsalProvider instance={msalInstance}>
            <FluentProvider theme={primerDarkTheme} style={{ height: "100%", minHeight: "100vh" }}>
                {isDebugRoute ? children : (
                    <MsalAuthenticationTemplate
                        interactionType={InteractionType.Redirect}
                        authenticationRequest={loginRequest}
                        loadingComponent={AuthLoading}
                        errorComponent={AuthError}
                    >
                        {children}
                    </MsalAuthenticationTemplate>
                )}
            </FluentProvider>
        </MsalProvider>
    );
};

